// FlightTimeDisplay — Presentational, timezone-aware flight time renderer.
//
// Extracted verbatim from `FlightLookup.js` as part of the behavior-preserving
// refactor (issue #240). This is a display concern, not container data-fetching:
// it resolves the airport's timezone via `airportTimezoneService` and formats
// the supplied UTC time string for that timezone. The async timezone lookup and
// the local `formattedTime` state are kept INSIDE this presentation subcomponent
// exactly as the source had them — the effect runs on mount and whenever the
// inputs change, falling back to the same "Loading..."/error strings the source
// produced. Wrapped in React.memo; render-only beyond its own display state.
//
// Props:
//   timeString  - UTC time string to format (or null)
//   airportCode - IATA/ICAO airport code used to resolve the display timezone
//   label       - 'departure' | 'arrival' (kept as an effect dependency, as in source)
//
// Validates: Requirements 3.3, 3.4, 1.2, 1.6

import React from 'react';
import airportTimezoneService from '../../../services/airportTimezoneService';

export const FlightTimeDisplay = React.memo(function FlightTimeDisplay({ timeString, airportCode, label }) {
  const [formattedTime, setFormattedTime] = React.useState('Loading...');

  React.useEffect(() => {
    const convertTime = async () => {
      try {
        // Normalize airport code - remove K prefix if present, use uppercase
        const normalizedCode = airportCode 
          ? airportCode.toUpperCase().replace(/^K/, '')
          : null;
        
        if (!normalizedCode || normalizedCode === 'N/A') {
          setFormattedTime('Time conversion error - invalid airport code');
          return;
        }

        const timezone = await airportTimezoneService.getAirportTimezone(normalizedCode);
        const date = new Date(timeString);
        
        // Verify timezone was found (not using fallback)
        if (timezone === 'America/New_York' && normalizedCode !== 'NYC' && !normalizedCode.startsWith('JFK') && !normalizedCode.startsWith('LGA') && !normalizedCode.startsWith('EWR')) {
          console.warn(`⚠️ Timezone lookup may have failed for ${airportCode} (normalized: ${normalizedCode}), got fallback EST`);
        }
        
        const result = date.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          timeZoneName: 'short',
          timeZone: timezone
        });
        
        setFormattedTime(result);
      } catch (error) {
        console.error('Error converting flight time:', error, { airportCode, timeString });
        setFormattedTime('Time conversion error');
      }
    };

    convertTime();
  }, [timeString, airportCode, label]);

  return formattedTime;
});

export default FlightTimeDisplay;
